import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Slider,
  Stack,
  Typography
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

import { TRACO } from '../utils/series';

/**
 * Simulador de cenário — a pergunta "e se".
 *
 * Quatro defeitos concretos moldaram este arquivo:
 *
 * 1. **Ausência virava medição.** O rótulo era `formatter(baseline || 0)`, então
 *    um indicador que ninguém mediu aparecia como "0,00 → 0,00" — com a seta de
 *    "não mudou" ao lado, porque `null` era tratado como estável. O produto
 *    afirmava que um número inexistente continuava igual.
 * 2. **Dois "0%" por controle.** A legenda dizia o valor e a régua repetia uma
 *    marca "0%" logo abaixo do polegar. Em repouso eram oito percentuais na tela
 *    do celular, e ao arrastar a marca continuava dizendo "0%" enquanto o valor
 *    já era outro — lida como valor atual.
 * 3. **Promessa que a conta não cumpre.** O texto convidava a ver "o score, a
 *    liquidez e a cobertura de dívida reagirem", mas o balanço nunca é ajustado:
 *    a liquidez é estruturalmente incapaz de reagir a qualquer controle daqui.
 * 4. **Resultado sem explicação.** Cinco números soltos, sem dizer que era
 *    hipótese, que nada tinha sido gravado, nem qual mês estava sendo projetado.
 */

const CONTROLES = [
  {
    chave: 'faturamentoPercent',
    rotulo: 'Faturamento',
    ajuda: 'Quanto você venderia a mais (ou a menos) por mês.',
    min: -0.2,
    max: 0.2,
    passo: 0.01
  },
  {
    chave: 'custosVariaveisPercent',
    rotulo: 'Custos variáveis',
    ajuda: 'Mercadoria, comissão, taxa de cartão e imposto sobre a venda.',
    min: -0.2,
    max: 0.2,
    passo: 0.01
  },
  {
    chave: 'custosFixosPercent',
    rotulo: 'Custos fixos',
    ajuda: 'Aluguel, pessoal, administrativo — sem os juros, que têm controle próprio.',
    min: -0.2,
    max: 0.2,
    passo: 0.01
  },
  {
    chave: 'despesasFinanceirasPercent',
    rotulo: 'Despesas financeiras',
    ajuda: 'Só os juros. O principal da dívida não se move por aqui.',
    min: -0.3,
    max: 0.3,
    passo: 0.01
  }
];

/** Sempre com sinal: "+10%" e "−10%" dizem a direção; "10%" deixa em dúvida. */
export const percentualComSinal = (valor) => {
  const pontos = Math.round(Number(valor || 0) * 100);
  if (pontos === 0) return 'sem mudança';
  return `${pontos > 0 ? '+' : '−'}${Math.abs(pontos)}%`;
};

const medido = (valor) => valor !== null && valor !== undefined && !Number.isNaN(valor);

/**
 * Comparação entre o número de hoje e o do cenário.
 *
 * Sem medição de um dos lados não há comparação: nem seta, nem cor, nem número
 * inventado — travessão dos dois lados e nada mais. A seta de "não mudou" para
 * `null` era a pior das saídas, porque afirmava estabilidade de uma medição que
 * nunca existiu.
 */
export const ParDeValores = ({ atual, cenario, formatar }) => {
  const comparavel = medido(atual) && medido(cenario);
  const tendencia = !comparavel
    ? null
    : cenario > atual + 0.005
      ? 'sobe'
      : cenario < atual - 0.005
        ? 'desce'
        : 'igual';

  const icone = tendencia === 'sobe'
    ? <TrendingUpIcon />
    : tendencia === 'desce'
      ? <TrendingDownIcon />
      : <TrendingFlatIcon />;

  const rotulo = `${medido(atual) ? formatar(atual) : TRACO} → ${medido(cenario) ? formatar(cenario) : TRACO}`;

  if (!comparavel) {
    return (
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {rotulo}
      </Typography>
    );
  }

  return (
    <Chip
      icon={icone}
      label={rotulo}
      size="small"
      color={tendencia === 'sobe' ? 'success' : tendencia === 'desce' ? 'error' : 'default'}
      variant="outlined"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    />
  );
};

const WhatIfPanel = ({
  adjustments = {},
  onAdjust,
  onSimulate,
  loading = false,
  scenario,
  highlights,
  erro = null,
  aoDispensarErro,
  competenciaProjetada = null
}) => {
  const algumAjuste = CONTROLES.some((c) => Number(adjustments[c.chave] || 0) !== 0);
  const podeRestaurar = algumAjuste || Boolean(scenario);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Simulador de cenário
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '70ch' }}>
          Mova os controles e veja o que aconteceria com o seu resultado, com o ponto de
          equilíbrio e com a capacidade de pagar a dívida. É uma projeção: nada aqui é
          gravado e o seu histórico não muda. O balanço não entra na simulação — liquidez e
          endividamento continuam mostrando a sua posição real.
        </Typography>

        {/* Erro nomeado, com a recuperação ao lado e no próprio painel. O diálogo
            do navegador que ficava aqui não dizia o que fazer e sumia sem deixar
            rastro. */}
        <Collapse in={Boolean(erro)} timeout={200} unmountOnExit>
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={aoDispensarErro}
            action={
              <Button color="inherit" size="small" onClick={onSimulate} disabled={loading}>
                Tentar de novo
              </Button>
            }
          >
            {erro}
          </Alert>
        </Collapse>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {CONTROLES.map((controle) => {
            const valor = Number(adjustments[controle.chave] || 0);
            return (
              <Grid item xs={12} md={3} key={controle.chave}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  id={`rotulo-${controle.chave}`}
                  sx={{ display: 'block' }}
                >
                  {controle.rotulo}: <strong>{percentualComSinal(valor)}</strong>
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', mb: 0.5, minHeight: 32 }}
                >
                  {controle.ajuda}
                </Typography>
                <Slider
                  value={valor}
                  min={controle.min}
                  max={controle.max}
                  step={controle.passo}
                  // Marca sem rótulo no zero: a régua precisa de referência, mas o
                  // texto "0%" embaixo do polegar era lido como valor atual e
                  // contradizia a legenda acima.
                  marks={[{ value: 0 }]}
                  // Sem trilha preenchida. O controle vai de −20% a +20%, e a
                  // trilha do MUI pinta do MÍNIMO até o polegar: em repouso, o
                  // "sem mudança" aparecia com metade da régua cheia, como se
                  // fosse um ajuste grande. Barra que mede a partir do lugar
                  // errado é pior que barra nenhuma.
                  track={false}
                  valueLabelDisplay="off"
                  disabled={loading}
                  aria-labelledby={`rotulo-${controle.chave}`}
                  getAriaValueText={percentualComSinal}
                  onChange={(_, novo) => onAdjust(controle.chave, novo)}
                />
              </Grid>
            );
          })}
        </Grid>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            disabled={loading || !algumAjuste}
            onClick={onSimulate}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Simulando…' : 'Simular cenário'}
          </Button>
          <Button
            variant="text"
            disabled={loading || !podeRestaurar}
            onClick={() => onAdjust('reset')}
            title="Volta os quatro controles para zero e descarta a projeção exibida"
          >
            Zerar os controles
          </Button>
          {/* Desabilitado tem que dizer por quê. Sem isso o usuário conclui que o
              produto travou. */}
          {!algumAjuste && !loading && (
            <Typography variant="caption" color="text.secondary">
              Mova pelo menos um controle para simular.
            </Typography>
          )}
        </Stack>

        <Collapse in={Boolean(scenario && highlights)} timeout={200} unmountOnExit>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2">Projeção hipotética</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {competenciaProjetada
                ? `Comparação com ${competenciaProjetada}, o mês mais recente que você lançou. `
                : 'Comparação com os números que você já lançou. '}
              Nada foi gravado.
            </Typography>
            <Grid container spacing={1.5}>
              {(highlights || []).map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.key}>
                  <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {item.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ display: 'block', mb: 0.75 }}
                    >
                      {item.unidade}
                    </Typography>
                    <ParDeValores
                      atual={item.current}
                      cenario={item.scenario}
                      formatar={item.formatar}
                    />
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Liquidez, endividamento e caixa sobre dívidas de curto prazo não aparecem aqui:
              eles vêm do balanço, que a simulação não ajusta.
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default WhatIfPanel;
